import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../authContext";
import { businessId, loadSetup } from "../inbox/businessSetup";
import { ChatRow, ago, loadAccount, loadInstagramChats, loadWhatsAppChats } from "../inbox/chats";

const DONE_KEY = "fd24.pendingDone.v1";

type Task = {
  id: string;
  kind: "instagram" | "call";
  name: string;
  detail: string;
  when: string;
  leadKey: string;
  channel: "instagram" | "whatsapp";
};

function loadDone(): string[] {
  try {
    const all = JSON.parse(localStorage.getItem(DONE_KEY) || "{}");
    const ids = all[businessId()];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function saveDone(ids: string[]) {
  const all = JSON.parse(localStorage.getItem(DONE_KEY) || "{}");
  all[businessId()] = ids;
  localStorage.setItem(DONE_KEY, JSON.stringify(all));
}

export default function PendingTasksPage() {
  const navigate = useNavigate();
  const { displayPhoneNumber } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [whatsappCount, setWhatsappCount] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [done, setDone] = useState<string[]>(loadDone);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const account = await loadAccount(displayPhoneNumber);
        const [instagram, whatsapp] = await Promise.all([
          loadInstagramChats(account.instagramId).catch(() => [] as ChatRow[]),
          account.phoneNumberId ? loadWhatsAppChats(account.phoneNumberId) : Promise.resolve([] as ChatRow[]),
        ]);
        if (cancelled) return;
        const setup = loadSetup();
        const byKey = new Map<string, ChatRow>();
        instagram.forEach((chat) => byKey.set(chat.key, chat));
        whatsapp.forEach((chat) => byKey.set(chat.key, chat));

        const instagramTasks: Task[] = instagram.map((chat) => ({
          id: `ig:${chat.key}`,
          kind: "instagram",
          name: chat.name,
          detail: chat.preview || "Reply on Instagram",
          when: ago(chat.at),
          leadKey: chat.key,
          channel: "instagram",
        }));

        const callTasks: Task[] = Object.entries(setup.leads)
          .filter(([, lead]) => lead.reminder)
          .map(([key, lead]) => {
            const chat = byKey.get(key);
            const reminder = lead.reminder!;
            return {
              id: `call:${key}`,
              kind: "call" as const,
              name: chat?.name || key.replace(/^wa:|^ig:/, ""),
              detail: reminder.note || "Call this customer",
              when: new Date(reminder.dueAt).toLocaleString(),
              leadKey: key,
              channel: key.startsWith("ig:") ? "instagram" as const : "whatsapp" as const,
            };
          });

        setWhatsappCount(whatsapp.length);
        setTasks(instagramTasks.concat(callTasks));
      } catch {
        if (!cancelled) setError("Could not load pending tasks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [displayPhoneNumber]);

  const openTask = (task: Task) => {
    navigate(`/events/conversations?lead=${encodeURIComponent(task.leadKey)}&channel=${task.channel}`);
  };

  const markDone = (id: string) => {
    const next = done.concat(id);
    saveDone(next);
    setDone(next);
  };

  const instagram = tasks.filter((task) => task.kind === "instagram" && !done.includes(task.id));
  const calls = tasks.filter((task) => task.kind === "call" && !done.includes(task.id));

  return (
    <div className="mx-auto max-w-4xl py-4">
      <h2 className="text-xl font-semibold text-gray-900">Pending tasks</h2>
      <p className="mt-1 text-sm text-gray-500">
        WhatsApp follow-ups can be sent by AI. Instagram replies and phone calls are waiting for a person.
      </p>

      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        WhatsApp: AI can send. {whatsappCount} conversation{whatsappCount === 1 ? "" : "s"} are not on this list.
      </div>

      {loading ? <p className="mt-6 text-sm text-gray-500">Loading tasks...</p> : null}
      {error ? <p className="mt-6 text-sm text-red-600">{error}</p> : null}

      {!loading && !error ? (
        <div className="mt-6 space-y-8">
          <TaskList
            title="Instagram"
            empty="No Instagram replies are waiting. A person sends these. AI does not."
            actionLabel="Open chat"
            tasks={instagram}
            onOpen={openTask}
            onDone={markDone}
          />
          <TaskList
            title="Calls"
            empty="No calls are waiting. Set a reminder on a lead in Inbox when someone needs a phone call."
            actionLabel="Open lead"
            tasks={calls}
            onOpen={openTask}
            onDone={markDone}
          />
        </div>
      ) : null}
    </div>
  );
}

function TaskList({
  title,
  empty,
  actionLabel,
  tasks,
  onOpen,
  onDone,
}: {
  title: string;
  empty: string;
  actionLabel: string;
  tasks: Task[];
  onOpen: (task: Task) => void;
  onDone: (id: string) => void;
}) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-900">
        {title} <span className="font-normal text-gray-400">({tasks.length})</span>
      </h3>
      {tasks.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{empty}</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {tasks.map((task) => (
            <li key={task.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{task.name}</p>
                <p className="truncate text-sm text-gray-500">{task.detail}</p>
              </div>
              <span className="text-xs text-gray-400">{task.when}</span>
              <button type="button" onClick={() => onOpen(task)} className="text-sm font-medium text-blue-600">
                {actionLabel}
              </button>
              <button type="button" onClick={() => onDone(task.id)} className="text-sm text-gray-500">
                Mark done
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
